
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { CircuitBreaker, calculateBackoff } from '@/utils/scalingStrategies';
import { logInfo, logError } from '@/utils/logger';

// Export the configured client
export const supabase = supabaseClient;

// API circuit breaker for the Supabase REST API
const apiCircuitBreaker = new CircuitBreaker(5, 60000);

// Base request wrapper for RESTful API calls with retry logic and circuit breaking
export const apiRequest = async (endpoint: string, method: string, data?: any, requiredRole?: string, retryAttempt = 0) => {
  try {
    // Check circuit breaker before making request
    if (!apiCircuitBreaker.canRequest()) {
      return { 
        success: false, 
        error: "Service temporarily unavailable, try again later", 
        status: 503 
      };
    }

    const { data: session } = await supabase.auth.getSession();
    
    if (!session?.session?.access_token) {
      return { success: false, error: "Authentication required", status: 401 };
    }
    
    // If a specific role is required, check user role
    if (requiredRole) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.session.user.id)
        .single();
        
      if (!profile || profile.role !== requiredRole) {
        return { success: false, error: "Insufficient permissions", status: 403 };
      }
    }
    
    // Add auth headers to all requests
    const headers = {
      'Authorization': `Bearer ${session.session.access_token}`,
      'Content-Type': 'application/json',
      'X-Request-Id': `req-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
    };
    
    // Make the actual request
    const response = await fetch(`/api/${endpoint}`, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined
    });
    
    // Record success with circuit breaker
    if (response.ok) {
      apiCircuitBreaker.recordSuccess();
      
      return {
        success: true,
        data: await response.json(),
        status: response.status
      };
    }
    
    // Handle retriable errors with backoff
    if (response.status >= 500 && retryAttempt < 3) {
      // Exponential backoff with jitter
      const backoffMs = calculateBackoff(retryAttempt);
      
      logInfo(`API retry (${retryAttempt + 1}/3) for ${endpoint} after ${backoffMs}ms`, 'api');
      
      await new Promise(resolve => setTimeout(resolve, backoffMs));
      
      // Retry the request
      return apiRequest(endpoint, method, data, requiredRole, retryAttempt + 1);
    }
    
    // Record failure with circuit breaker for server errors
    if (response.status >= 500) {
      apiCircuitBreaker.recordFailure();
    }
    
    return { 
      success: false, 
      error: await response.text(),
      status: response.status
    };
  } catch (e) {
    // Record failure and check if we should retry
    apiCircuitBreaker.recordFailure();
    
    if (retryAttempt < 3) {
      const backoffMs = calculateBackoff(retryAttempt);
      logInfo(`API retry (${retryAttempt + 1}/3) for ${endpoint} after ${backoffMs}ms`, 'api');
      
      await new Promise(resolve => setTimeout(resolve, backoffMs));
      
      // Retry the request
      return apiRequest(endpoint, method, data, requiredRole, retryAttempt + 1);
    }
    
    logError(`API error (${endpoint}):`, e);
    return { success: false, error: e, status: 500 };
  }
};
